import React, { useState, useEffect } from 'react';
import { updateStaff, fetchRolesAPI, fetchSexes as fetchSexesAPI } from '../utils/api';
import { mapErrorMessage } from '../utils/errorMapping';
import { ADMIN_ROLE, FINANCIAL_STAFF, FINANCIAL_VIEWER_STAFF } from '../utils/roles';
import './_Checkbox.css';
import './_Roles.css';

const EditStaffModal = ({ onClose, onStaffUpdated, staff }) => {
    const [firstName, setFirstName] = useState(staff.first_name || '');
    const [lastName, setLastName] = useState(staff.last_name || '');
    const [email, setEmail] = useState(staff.email || '');
    const [phone, setPhone] = useState(staff.phone || '');
    const [sex, setSex] = useState(staff.sex ? staff.sex.id : '');
    const [description, setDescription] = useState(staff.description || '');
    const [roles, setRoles] = useState([]);
    const [sexes, setSexes] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState(staff.roles.map(r => r.id) || []);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadData = async () => {
            try {
                const sexesData = await fetchSexesAPI();
                const sexesArray = Object.keys(sexesData.data).map(key => ({
                    id: parseInt(key, 10),
                    name: sexesData.data[key]
                }));
                setSexes(sexesArray);

                const rolesData = await fetchRolesAPI();
                setRoles(rolesData.data);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        loadData();
    }, []);

    const handleRoleChange = (e) => {
        const roleId = parseInt(e.target.value, 10);
        const isChecked = e.target.checked;

        if (roleId === ADMIN_ROLE) {
            if (isChecked) {
                setSelectedRoles([ADMIN_ROLE]);
            } else {
                setSelectedRoles([]);
            }
        } else {
            let newRoles = selectedRoles.filter(r => r !== ADMIN_ROLE);
            if (isChecked) {
                newRoles = [...newRoles, roleId];
            } else {
                newRoles = newRoles.filter(id => id !== roleId);
            }
            setSelectedRoles(newRoles);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        let finalRoles = [...selectedRoles];

        if (finalRoles.includes(FINANCIAL_VIEWER_STAFF) && finalRoles.length === 1) {
            setError('Financial Viewer cannot be the only role.');
            return;
        }

        if (!firstName || !lastName || !email || finalRoles.length === 0) {
            setError('Please fill in all fields and select at least one role.');
            return;
        }

        try {
            await updateStaff(staff.id, {
                first_name: firstName,
                last_name: lastName,
                email: email,
                phone: phone || null,
                sex: parseInt(sex, 10),
                description: description,
                roles: finalRoles
            });
            onStaffUpdated();
            onClose();
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const isAdminSelected = selectedRoles.includes(ADMIN_ROLE);
    const isFinancialSelected = selectedRoles.includes(FINANCIAL_STAFF);
    const isFinancialViewerSelected = selectedRoles.includes(FINANCIAL_VIEWER_STAFF);
    const otherRolesSelected = selectedRoles.some(r => r !== ADMIN_ROLE);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="modal-close" onClick={onClose}>X</button>
                <h2>Edit Staff Member</h2>
                {error && <p className="error">{error}</p>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <input
                            id="firstName"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="First Name"
                        />
                    </div>
                    <div className="form-group">
                        <input
                            id="lastName"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Last Name"
                        />
                    </div>
                    <div className="form-group">
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                        />
                    </div>
                    <div className="form-group">
                        <input
                            id="phone"
                            type="text"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="Phone"
                        />
                    </div>
                    <div className="form-group">
                        <div className="select-container">
                            <select id="sex" name="sex" value={sex} onChange={(e) => setSex(e.target.value)}>
                                <option value="" disabled>Sex</option>
                                {sexes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="form-group">
                        <textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Description"
                        />
                    </div>
                    <div className="form-group">
                        <p>Roles</p>
                        <div className="checkbox-group">
                            {roles.filter(r => r.id === ADMIN_ROLE).map(role => (
                                <div key={role.id} className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        id={`role-${role.id}`}
                                        value={role.id}
                                        checked={selectedRoles.includes(role.id)}
                                        onChange={handleRoleChange}
                                        disabled={otherRolesSelected}
                                    />
                                    <label htmlFor={`role-${role.id}`}>{role.name}</label>
                                </div>
                            ))}
                            {roles.filter(r => r.id !== ADMIN_ROLE).map(role => (
                                <div key={role.id} className={`checkbox-item ${role.id === FINANCIAL_VIEWER_STAFF && isFinancialSelected ? 'dimmed' : ''}`}>
                                    <input
                                        type="checkbox"
                                        id={`role-${role.id}`}
                                        value={role.id}
                                        checked={selectedRoles.includes(role.id)}
                                        onChange={handleRoleChange}
                                        disabled={isAdminSelected || (role.id === FINANCIAL_VIEWER_STAFF && isFinancialSelected) || (role.id === FINANCIAL_STAFF && isFinancialViewerSelected)}
                                    />
                                    <label htmlFor={`role-${role.id}`}>{role.name}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='actions-centered'>
                        <button onClick={onClose} className='cancel-button text-button'>Cancel</button>
                        <button type="submit" className="submit-button text-button">Update</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditStaffModal;